/*
 * @Description: 知识库状态管理
 * @Author: ljrong
 * @Date: 2024-07-09 15:48:36
 * @LastEditors: Lxc 1533761120@qq.com
 * @LastEditTime: 2024-09-02 17:29:20
 */

import { defineStore } from 'pinia'
import { useWorkspaceStore } from '@/store'
import type { KnowlegeFile, KnowlegeFileParams } from '@/types/knowledge/file'
import type { KnowlegeFolder } from '@/types/knowledge/folder'
import { KnowledgeBaseLibType } from '@/types/knowledge/common'
import type { LibStatusItem } from '@/types/knowledge/lib'

import { getCollectFileList, getKnowledegFileList, setCollectFile } from '@/api/knowledge/file'
import { getKnowledegFolderFileCount, getKnowledegFolderList } from '@/api/knowledge/folder'
import { getLibStatus as getKnowledgeLibStatus, getLibFileRemain } from '@/api/knowledge/lib'
import { openWindowWithWorkspace } from '@/utils/tool'

interface ToppingItem {
  name: string; ossUrl: string; id: number; status: number
}

// import { router } from '@/router'
function addLevelToTree(treeData, level = 1) {
  // 检查treeData是否为数组，如果不是数组，则返回
  if (!Array.isArray(treeData)) {
    return
  }

  treeData.forEach((node) => {
    // 如果节点没有level属性，则添加level属性
    if (!node.hasOwnProperty('level')) {
      node.level = level
    }

    // 如果当前节点有children属性，递归调用该函数
    if (node.child && Array.isArray(node.child)) {
      addLevelToTree(node.child, level + 1)
    }
  })

  return treeData
}
/**
 * 知识库数据获取流程
 *1、获取当前企业/个人空间下的知识库列表 main-header
 *2、获取知识库下的文件夹列表 folder
 *3、获取知识库下的文件列表 data-list
 */

const useKnowledgeStore = defineStore('KnowledgeStore', {
  state: () => {
    return {
      /**
       * 当前空间下全部知识库列表
       */
      libStatus: <LibStatusItem[]>[],
      // 平台知识库
      platformLib: {} as LibStatusItem,
      // 平台知识库文件夹
      platformFolderList: [] as KnowlegeFolder[],
      /**
       * 当前企业知识库 id
       */
      currentEnterpriseLibId: 0,
      /**
       * 搜索关键字
       */
      keyword: '',
      /**
       * 当前文件夹id
       */
      currentFolderId: 0,
      /**
       * 选中的文件id
       */
      selectionIds: [],
      /**
       * 当前知识库id
       */
      currentLibId: 0,
      /**
       * 可见列，用于控制table的列是否可见
       */
      visibleColumns: [],
      /**
       * 搜索类型
      */
      searchType: 0, // 搜索类型：0:默认 1:推荐，2:最新，3:订阅，4:我的
      /**
       * 当前知识库的文件夹列表
       */
      folderList: [] as KnowlegeFolder[],
      folderListLoading: true,
      /**
       * 文件夹总文件书数，通过文件夹列表的fileCount计算出来
       */
      folderFileCount: 0,
      /**
       * 平台知识库文件夹总文件数
       */
      platformFolderFileCount: 0,
      /**
       * list数据
       */
      fileList: [] as KnowlegeFile[],
      fileListTotal: 0,
      fileListLoading: true,
      /**
       * 展示ai助手模块
       */
      aiAssistantVisiable: false,
      /**
       * 批量删除
       *
       */
      isMutilpleDeleteVisiable: false,
      /**
       * 批量移动
       *
       */
      isMutilpleMoveVisiable: false,
      /**
       * 批量复制
       *
       */
      isMutilpleCopyVisiable: false,
      /**
       * 单前知识库文件容量
       */
      filesRemain: {
        allLimit: 0, // 总限制大小：文件为个数，大小单位为b
        limit: 0, // 剩余大小：文件为个数，大小单位为b
        type: 0, // 剩余类型：0：文件个数 1：文件大小
      },
      // 置顶列表
      toppingList: [] as ToppingItem[],
    }
  },
  actions: {
    setKeyword(keyword: string) {
      this.keyword = keyword
    },
    setSearchType(type: number) {
      this.searchType = type
    },
    setCurrentFolderId(id: number) {
      this.currentFolderId = id
    },
    setSelectionIds(ids: number[]) {
      this.selectionIds = ids
    },
    setCurrentLibId(id: number) {
      this.currentLibId = id
    },
    setFolderList(list: KnowlegeFolder[]) {
      this.folderList = list
    },
    setFolderFileCount(count: number) {
      this.folderFileCount = count
    },
    setLibStatus(list: LibStatusItem[]) {
      this.libStatus = list
    },
    setPlatformLib(lib: LibStatusItem) {
      this.platformLib = lib
    },
    setPlatformFolderList(list: KnowlegeFolder[]) {
      this.platformFolderList = list
    },
    async getLibStatus() {
      return getKnowledgeLibStatus({ enterpriseId: this.enterpriseId }).then((res) => {
        this.setPlatformLib(res.find(item => item.libType === KnowledgeBaseLibType.Platform)!)

        let libs: LibStatusItem[] = []
        if (this.isPersonal) {
          libs = res.filter(item => item.libType === KnowledgeBaseLibType.Personal)
        } else {
          const enterprise = res.find(item => item.libType === KnowledgeBaseLibType.Enterprise)!
          const myUpload = {
            ...enterprise,
            name: '我的上传',
            type: 4,
          }
          libs = [enterprise, myUpload]
        }
        this.setLibStatus(libs)
        this.setCurrentLibId(libs[0].knowledgeBaseId)
      }).catch(() => {
        window.$message?.error('获取知识库状态失败，初始化失败')
      }).finally(() => {
      })
    },
    async getPlatformFolderList(params?: object) {
      return getKnowledegFolderList({
        knowledgeBaseId: this.platformLibId,
        ...params,
      }).then((res) => {
        const levelList = addLevelToTree(res)
        this.setPlatformFolderList(levelList)
        return levelList
      })
    },
    getPlatformFolderCount(params?: object) {
      return getKnowledegFolderFileCount({
        knowledgeBaseId: this.platformLibId,
        ...params,
      }).then((res) => {
        this.platformFolderFileCount = res
        console.log('platformFolderFileCount ', res)
      })
    },
    getFolderList() {
      this.folderListLoading = true
      getKnowledegFolderList({
        enterpriseId: this.enterpriseId,
        knowledgeBaseId: this.currentLibId,
        type: this.searchType,
        keyword: this.keyword,
      }).then((res) => {
        this.setFolderList(res)

        // 计算文件夹下文件总数
        const folderFileCount = res.reduce((total, item) => total + (item.fileCount || 0), 0)
        this.setFolderFileCount(folderFileCount)

        // this.getFileList({ dirId: null })
      }).finally(() => {
        this.folderListLoading = false
      })
    },
    getFileList(params: Partial<KnowlegeFileParams> = {}) {
      const defaultParams = {
        keyword: this.keyword,
        enterpriseId: this.enterpriseId,
        knowledgeBaseId: this.currentLibId,
        dirIds: this.currentFolderId === 0 ? null : [this.currentFolderId],
        type: this.searchType,
      }
      this.fileListLoading = true
      return getKnowledegFileList({ ...defaultParams, ...params }).then((res) => {
        this.fileList = res.records
        this.fileListTotal = res.total
        return res
      }).catch((error) => {
        window.$message?.error(error.resultMsg)
      }).finally(() => {
        this.fileListLoading = false
      })
    },
    setAiAssitantVisiable(visiable: boolean) {
      this.aiAssistantVisiable = visiable
    },
    setMutilpleDeleteVisiable(visiable: boolean) {
      this.isMutilpleDeleteVisiable = visiable
    },
    setMutilpleMoveVisiable(visiable: boolean) {
      this.isMutilpleMoveVisiable = visiable
    },
    setMutilpleCopyVisiable(visiable: boolean) {
      this.isMutilpleCopyVisiable = visiable
    },
    showPermissionTips() {
      window.$message?.error('无知识库操作权限')
    },
    setFilesRemain(data: { allLimit: number; limit: number; type: number }) {
      this.filesRemain = data
    },
    getFilesRemain() {
      getLibFileRemain(this.currentLibId).then((res) => {
        this.setFilesRemain(res)
      })
    },
    jumpFileDetail(fileId: number, fileStatus: number, question?: string) {
      // console.log(fileId, fileStatus, question)
      if (fileStatus === 2 || fileStatus === 3) {
        const q = question ? encodeURIComponent(question.replace(/%/g, '%25')) : ''
        openWindowWithWorkspace(`/knowledge-file-detail/${fileId}?q=${q}`)
        // router.push({
        //   path: `/knowledge-file-detail/${fileId}`,
        //   query: { q: encodeURIComponent(question || '') },
        // })
      }
    },
    getToppingList() {
      getCollectFileList({
        enterpriseId: this.enterpriseId,
        pageSize: 20,
      }).then((res) => {
        this.setToppingList(res.records as ToppingItem[])
      })
    },
    setToppingList(data: ToppingItem[]) {
      this.toppingList = data
    },
    setFileToppingStatus(file: {
      collectFlag: number
      id: number
      ossUrl: string
      name: string
    }) {
      // 设置成置顶
      const isToTopping = file.collectFlag === 0
      setCollectFile({
        enterpriseId: this.enterpriseId,
        fileId: file.id,
        knowledgeBaseId: this.currentLibId,
        collectFlag: isToTopping ? 1 : 0,
      }).then(() => {
        if (isToTopping) {
          // 更新置顶列表 新增
          this.setToppingList([...this.toppingList, {
            ...file,
            collectFlag: 1,
          }])
          // 更新列表状态
          this.fileList = this.fileList.map((item) => {
            if (item.id === file.id) {
              return {
                ...item,
                collectFlag: 1,
              }
            }
            return item
          })
          window.$message?.success('置顶成功')
        } else {
          // 更新置顶列表 删除
          this.setToppingList(this.toppingList.filter(item => item.id !== file.id))
          // 更新列表状态
          this.fileList = this.fileList.map((item) => {
            if (item.id === file.id) {
              return {
                ...item,
                collectFlag: 0,
              }
            }
            return item
          })
          window.$message?.success('取消置顶成功')
        }
      }).catch((error) => {
        window.$message?.error(error.resultMsg)
      })
    },
    reset() {
      this.keyword = ''
      this.currentFolderId = 0
      this.selectionIds = []
      this.currentLibId = 0
      this.visibleColumns = []
      this.folderList = []
      this.folderFileCount = 0
      this.fileList = []
      this.fileListTotal = 0
      this.aiAssistantVisiable = false
      this.isMutilpleDeleteVisiable = false
      this.isMutilpleMoveVisiable = false
      this.isMutilpleCopyVisiable = false
      this.filesRemain = {
        allLimit: 0,
        limit: 0,
        type: 0,
      }
    },
  },
  getters: {
    enterpriseId: () => useWorkspaceStore().workspaceId as number,
    isPersonal: () => useWorkspaceStore().isPersonal,
    platformLibId: state => state.platformLib.knowledgeBaseId as number || 1,
    hasKnowledgePermission() {
      const workspaceStore = useWorkspaceStore()

      // 个人空间下有全部都权限
      if (workspaceStore.isPersonal) {
        return true
      } else {
        // 企业空间下去要判断 是否有管理知识库的权限
        const hasPermissionEnterprise = workspaceStore.workspaceList.filter(item => item.enterpriseRole === 1 || (item.permissions && item.permissions?.includes(1)))
        return !!hasPermissionEnterprise.find(item => item.workspaceId === workspaceStore.workspaceId)
      }
    },
  },
})

export default useKnowledgeStore
